"use client"

import { cn } from "@/lib/utils"
import { AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react"

export interface AlertItem {
  level: "danger" | "warning" | "safe"
  label: string
  value?: string
  detail?: string
}

const LEVEL_STYLES = {
  danger: {
    dot: "bg-red-500",
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-800/40",
    text: "text-red-700 dark:text-red-300",
    icon: AlertTriangle,
  },
  warning: {
    dot: "bg-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800/40",
    text: "text-amber-700 dark:text-amber-300",
    icon: AlertCircle,
  },
  safe: {
    dot: "bg-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-950/30",
    border: "border-emerald-200 dark:border-emerald-800/40",
    text: "text-emerald-700 dark:text-emerald-300",
    icon: CheckCircle2,
  },
}

export function AlertCard({
  title,
  items,
  className,
}: {
  title?: string
  items: AlertItem[]
  className?: string
}) {
  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {title && (
        <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-semibold text-foreground">{title}</span>
          {items.filter(i => i.level === "danger").length > 0 && (
            <span className="text-micro px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 font-medium ml-auto">
              {items.filter(i => i.level === "danger").length} 项高风险
            </span>
          )}
        </div>
      )}

      <div className="p-2 space-y-1.5">
        {items.map((item, i) => {
          const style = LEVEL_STYLES[item.level]
          const Icon = style.icon

          return (
            <div
              key={i}
              className={cn(
                "flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-all animate-in fade-in slide-in-from-left-1",
                style.bg, style.border,
              )}
              style={{ animationDelay: `${i * 60}ms`, animationFillMode: "both" }}
            >
              <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", style.text)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className={cn("text-sm font-medium", style.text)}>
                    {item.label}
                  </span>
                  {item.value && (
                    <span className={cn("text-sm font-mono font-bold", style.text)}>
                      {item.value}
                    </span>
                  )}
                </div>
                {item.detail && (
                  <p className="text-caption text-muted-foreground mt-0.5">{item.detail}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
